import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);

dayjs.extend(timezone);

/**
 * Formats a timestamp in a specific timezone.
 * @param {string} inputTimestamp - The ISO timestamp to format
 * @param {string} timeZone - The IANA timezone string
 * @return {string} Formatted timestamp like "3:07 PM Sep 4, 2025"
 */
export function formatTimestampInTimezone(
  inputTimestamp: string,
  timeZone: string
): string {
  if (!inputTimestamp || !timeZone) {
    return "";
  }

  const date = dayjs.utc(inputTimestamp).tz(timeZone);

  if (!date.isValid()) {
    return "";
  }

  const formatted = date.format("h:mm A MMM D, YYYY");

  return `${formatted}`;
}

/**
 * Formats a raw/naive timestamp (no timezone conversion).
 * Used for facility local time from rawEventInfo.data.Timestamp.
 * @param {string} inputTimestamp - The naive timestamp string (no Z suffix)
 * @return {string} Formatted timestamp like "3:07 PM Sep 4, 2025"
 */
export function formatRawTimestamp(inputTimestamp: string): string {
  if (!inputTimestamp) {
    return "";
  }

  const date = dayjs(inputTimestamp);

  if (!date.isValid()) {
    return "";
  }

  return date.format("h:mm A MMM D, YYYY");
}

// This function formats a timestamp in the format of "3:07 PM Sep 4, 2025"
// If this format is changed make sure to update the parsing in DeviceImageListItemBarTitle.tsx
export function formatTimestamp(inputTimestamp: string): string {
  if (!inputTimestamp) {
    return "";
  }

  const date = new Date(inputTimestamp);

  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const time = timeFormatter.format(date);
  const day = dateFormatter.format(date);

  return `${time} ${day}`;
}

export function convertDateToUTC(localDate: Date | null) {
  if (!localDate) {
    return null;
  }

  // Get the local timezone offset in minutes.
  const offsetMinutes = localDate.getTimezoneOffset();

  // Convert local date to UTC by adding the offset
  // For GMT+3, offsetMinutes = -180, so we substract 180 minutes to the local date.
  // For GMT-8, offsetMinutes = 480, so we add 480 minutes to the local date.
  const utcTimestamp = new Date(localDate.getTime() + offsetMinutes * 60000);

  return utcTimestamp;
}
